import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { keys } from '../../api/keys'
import { useEntitlement } from '../../platform/access/useEntitlement'
import { localToday } from '../../platform/localDate'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { useCategories } from '../categories/useCategories'
import type { CategoryMap } from '../cashflow/cashflowCategories'
import { TABLE_LIMIT, useReportQuery } from '../cashflow/useCashflow'
import { MerchantPickerDialog } from '../merchants/picker/MerchantPickerDialog'
import { useMerchantNames } from '../merchants/useMerchants'
import { useFilterOptions } from '../transactions/useFilterOptions'
import { emitConfigChanged, emitEmpty, emitOpened, emitRun } from './reportsAnalytics'
import { filterFieldCount, presetSpec, queryRequest, readSpec, writeSpec, type ChartType, type FilterNames, type PresetId, type ReportSpec } from './reportSpec'
import { CHART_SERIES, isEmptyQuery } from './reportView'
import { ReportsScreen } from './ReportsScreen'

/**
 * The `/reports` route. The screen is presentational; this owns what it must not: the URL (the whole
 * report lives in the query string, R10), the one request, the entitlement and the analytics.
 *
 * A spec change PUSHES, so Back restores the report before it; a chart-type change REPLACES, since it
 * only re-draws (M8-R2: the chart type is not in the query key, so it never refetches). A free user
 * fetches nothing (P10).
 */
export function Reports() {
  const [params, setParams] = useSearchParams()
  // One day for the whole mount: a render across midnight must not re-key the query.
  const today = useMemo(() => localToday(), [])
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, [])
  const bp = useBreakpoint()
  const entitled = useEntitlement('cashflow_details')

  const spec = useMemo(() => readSpec(params, today), [params, today])

  // A malformed URL is rewritten to the canonical one. REPLACE: the bad URL was never a place to go Back to.
  const canonical = writeSpec(spec).toString()
  const raw = params.toString()
  useEffect(() => {
    if (raw !== canonical) setParams(new URLSearchParams(canonical), { replace: true })
  }, [raw, canonical, setParams])

  const request = queryRequest(spec, tz)
  const table = useReportQuery({ request, limit: TABLE_LIMIT, enabled: entitled, isEmpty: isEmptyQuery })
  // The chart's own request: the top CHART_SERIES rows and the server's "Other". A second cache entry, because
  // one response cannot be both every row (the table) and the top few (the chart). The chart type is in
  // neither key, so switching it refetches neither.
  const chart = useReportQuery({ request, limit: CHART_SERIES, enabled: entitled })

  const categoriesState = useCategories()
  const categoryRows = categoriesState.status === 'success' ? categoriesState.data : null
  const names = useMemo<CategoryMap>(
    () => new Map((categoryRows ?? []).map((c) => [c.id, { name: c.name, parentId: c.parent_id ?? null, icon: c.icon ?? null }])),
    [categoryRows],
  )
  const groups = useMemo(
    () => (categoryRows ?? []).filter((c) => (c.parent_id ?? null) === null).map((c) => ({ id: c.id, name: c.name })),
    [categoryRows],
  )
  const options = useFilterOptions()
  const merchantName = useMerchantNames(spec.filters.merchants)
  const filterNames = useMemo<FilterNames>(() => {
    const label = (list: { id: string; label: string }[]) => (id: string) => list.find((o) => o.id === id)?.label ?? null
    return {
      group: (id) => names.get(id)?.name ?? null,
      merchant: (id) => {
        const m = merchantName(id)
        return m.status === 'found' ? m.merchant.name : null
      },
      account: label(options.accounts),
      owner: label(options.owners),
    }
  }, [names, merchantName, options.accounts, options.owners])

  const [picking, setPicking] = useState(false)

  const push = useCallback((next: ReportSpec) => setParams(writeSpec(next)), [setParams])
  const onSpecChange = useCallback(
    (next: ReportSpec, control: 'range' | 'columns' | 'filter' | 'rows' | 'preset') => {
      emitConfigChanged(control)
      push(next)
    },
    [push],
  )
  const onPreset = useCallback(
    (id: PresetId) => {
      emitConfigChanged('preset')
      push(presetSpec(id, today))
    },
    [push, today],
  )
  const onChartChange = useCallback(
    (chart: ChartType) => {
      emitConfigChanged('chart_type')
      setParams(writeSpec({ ...spec, chart }), { replace: true })
    },
    [setParams, spec],
  )

  const opened = useRef(false)
  useEffect(() => {
    if (opened.current) return
    opened.current = true
    emitOpened(!entitled)
  }, [entitled])

  // Once per key, on the response that answers it. The key is the request's own parameters.
  const key = JSON.stringify(keys.cashflow.query({ ...request, limit: TABLE_LIMIT }))
  const reported = useRef<string | null>(null)
  const status = table.state.status
  // Every row the server listed, plus the rows it folded into Other: a count, never money.
  const rowCount = table.state.status === 'success' ? table.state.data.rows.length + table.state.data.remainder.rowCount : null
  const fieldCount = filterFieldCount(spec.filters)
  useEffect(() => {
    if (reported.current === key) return
    if (status === 'success' && rowCount !== null) {
      reported.current = key
      emitRun({ columns: spec.columns, range: spec.range, rows: spec.rows, rowCount, filterFieldCount: fieldCount })
    } else if (status === 'empty') {
      reported.current = key
      emitEmpty(fieldCount)
    }
  }, [key, status, rowCount, spec.columns, spec.range, spec.rows, fieldCount])

  return (
    <>
      <ReportsScreen
        spec={spec}
        request={request}
        today={today}
        phone={bp === 'phone'}
        tablet={bp === 'tablet'}
        table={table}
        chart={chart}
        filterNames={filterNames}
        names={names}
        groups={groups}
        groupsLoading={categoriesState.status === 'loading'}
        options={options}
        onSpecChange={onSpecChange}
        onPreset={onPreset}
        onChartChange={onChartChange}
        onPickMerchant={() => setPicking(true)}
      />
      <MerchantPickerDialog
        open={picking}
        onClose={() => setPicking(false)}
        value={null}
        onPick={(id) => {
          if (id === null) return
          emitConfigChanged('filter')
          push({ ...spec, filters: { ...spec.filters, merchants: [...new Set([...spec.filters.merchants, id])].sort() } })
        }}
      />
    </>
  )
}
